import React, { useRef, useState, useEffect, useMemo, KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';

import { m } from 'framer-motion';
import classNames from 'classnames';
import CaretDown from '@/components/Icons/CaretDown';
import Check from '@/components/Icons/Check';
import usePopover from '@/hooks/usePopover';
import Label from '@/components/DesignLibrary/Atoms/shared/Label';
import { useQuestionnaireContext } from '@/components/Questionnaire/QuestionnaireProvider';
import PlusCircle from '@components/Icons/PlusCircle';
import Button from '@components/DesignLibrary/Button';

import s from './CreditorSelect.module.scss';

type Option = {
  key: string;
  label: string;
  description?: string;
};

type CreditorSelectProps = {
  label: string;
  compact?: boolean;
  hideLabel?: boolean;
  description?: string;
  disabled?: boolean;
  value?: string | null;
  placeholder?: string;
  error?: string;
  optional?: boolean;
  hint?: string;
  onChange: (value: string) => void;
  onFocus?: () => void;
  native?: boolean;
  ignoreKeyboardNavigation?: boolean;
  onClickAddCreditor?: () => void;
};

const MotionCaret = m(CaretDown);

const CreditorSelect: React.FC<CreditorSelectProps> = ({
  label,
  compact,
  hideLabel,
  description,
  value,
  placeholder,
  disabled,
  error,
  optional,
  hint,
  onChange,
  onClickAddCreditor,
  onFocus,
  native,
  ignoreKeyboardNavigation,
}) => {
  const {
    isOpen,
    setIsOpen,
    menuRef,
    triggerRef,
  } = usePopover('bottom-left', true);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [checkedIndex, setCheckedIndex] = useState(-1);
  const [filter, setFilter] = useState('');
  const listRef = useRef<HTMLUListElement>(null);
  const labelId = `label-${label}`;
  const isMouseInteractionRef = useRef(false);
  const { creditors } = useQuestionnaireContext();

  const creditorOptions: Option[] = useMemo(
    () =>
      creditors.map(creditor => ({
        description: `${creditor.address.line1}${creditor.attention ? ` - ${creditor.attention}` : ''}`,
        key: creditor.id,
        label: creditor.name,
      })),
    [creditors],
  );

  const filteredOptions = useMemo(
    () => creditorOptions.filter(o => String(o.label || '').toLowerCase().includes(filter.toLowerCase())),
    [creditorOptions, filter],
  );

  // check if select triggered by mouse
  const handleMouseDown = (): void => {
    isMouseInteractionRef.current = true;
  };

  const handleFocus = (): void => {
    if (onFocus) onFocus();
    if (!isMouseInteractionRef.current) {
      setIsOpen(true);
    }
  };

  const handleClick = (): void => {
    if (isMouseInteractionRef.current) {
      setIsOpen((prev) => !prev);
    } else {
      setIsOpen(true);
    }
    isMouseInteractionRef.current = false;
  };

  useEffect(() => {
    const optionIndex = filteredOptions.findIndex(o => o.key === value);
    setSelectedIndex(optionIndex);
    setCheckedIndex(optionIndex);
  }, [value, filteredOptions]);

  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
    }
  }, [disabled, setIsOpen]);

  useEffect(() => {
    if (listRef.current && selectedIndex >= 0) {
      const optionElement = listRef.current.children[selectedIndex];
      const listRect = listRef.current.getBoundingClientRect();
      const optionRect = optionElement.getBoundingClientRect();
      if (optionRect.bottom > listRect.bottom) {
        listRef.current.scrollTop += optionRect.bottom - listRect.bottom;
      } else if (optionRect.top < listRect.top) {
        listRef.current.scrollTop -= listRect.top - optionRect.top;
      }
    }
  }, [selectedIndex]);

  useEffect(() => {
    if (isOpen && listRef.current && checkedIndex >= 0) {
      const optionElements = listRef.current.querySelectorAll('li');
      if (optionElements.length > checkedIndex) {
        const optionElement = optionElements[checkedIndex];
        const listRect = listRef.current.getBoundingClientRect();
        const optionRect = optionElement.getBoundingClientRect();
        if (optionRect.bottom > listRect.bottom) {
          listRef.current.scrollTop += optionRect.bottom - listRect.bottom;
        } else if (optionRect.top < listRect.top) {
          listRef.current.scrollTop -= listRect.top - optionRect.top;
        }
      }
    }
  }, [isOpen, checkedIndex]);

  const handleOptionSelect = (checkedValue: string): void => {
    const option = filteredOptions.find(o => o.key === checkedValue);
    if (!option) {
      return;
    }
    const optionIndex = filteredOptions.findIndex(o => o.key === checkedValue);
    setCheckedIndex(optionIndex);
    onChange(checkedValue);
    setTimeout(() => {
      setIsOpen(false);
    }, 150);
  };

  const handleWrapperBlur = (e: React.FocusEvent<HTMLDivElement>): void => {
    const focusedElement = e.relatedTarget as Node;

    if (
      focusedElement !== triggerRef.current && !menuRef.current?.contains(focusedElement)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (!isOpen) setSelectedIndex(-1);
    setFilter('');
  }, [isOpen]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (!isOpen) return;

    switch (event.key) {
    case 'Escape':
      setIsOpen(false);
      break;
    case 'ArrowDown':
      event.preventDefault();
      setSelectedIndex(prevIndex => {
        const nextIndex = (prevIndex + 1) % filteredOptions.length;
        return nextIndex;
      });
      break;
    case 'ArrowUp':
      event.preventDefault();
      setSelectedIndex(prevIndex => {
        const nextIndex = (prevIndex - 1 + filteredOptions.length) % filteredOptions.length;
        return nextIndex;
      });
      break;
    case 'Enter':
      if (selectedIndex > -1) {
        handleOptionSelect(filteredOptions[selectedIndex].key);
        event.preventDefault();
      }
      break;
    default:
      break;
    }
  };

  if (native) {
    return (
      <div className={s.wrapper}>
        {label && !hideLabel && (
          <Label
            label={label}
            required={!optional}
            description={description}
            compact={compact}
            id={labelId}
          />
        )}
        <select
          id={labelId}
          value={value ?? '#selectAnOption#'}
          onChange={e => handleOptionSelect(e.target.value)}
          className={s.nativeSelect}
          disabled={disabled}
          onFocus={onFocus}
          tabIndex={ignoreKeyboardNavigation ? -1 : 0}
        >
          <option value="#selectAnOption#" disabled>
            {placeholder || 'Select an option'}
          </option>
          {creditorOptions?.map(option => (
            <option key={option.key} value={option.key}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div className={s.selectWrapper}>
      {(label || description) && !hideLabel && <Label
        label={label}
        required={!optional}
        description={description}
        compact={compact}
        id={labelId}
      />}
      <select
        className={s.hiddenNativeSelect}
        value={value ?? ''}
        onChange={e => onChange(e.target.value)}
        required={!optional}
        aria-hidden="true"
        tabIndex={-1}
      >
        <option disabled></option>
        {creditorOptions.map(option => (
          <option key={option.key} value={option.key}>
            {option.label}
          </option>
        ))}
      </select>
      <div
        tabIndex={-1}
        className={s.wrapper}
        onKeyDown={handleKeyDown}
        onBlur={handleWrapperBlur}
      >
        <button
          type="button"
          className={s.button}
          onMouseDown={handleMouseDown}
          onFocus={handleFocus}
          onClick={handleClick}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-labelledby={labelId}
          disabled={disabled}
          ref={triggerRef}
          tabIndex={ignoreKeyboardNavigation ? -1 : 0}
        >
          <div className={classNames(s.trigger, !!error && s.errorState)}>
            <div className={s.triggerLabels}>
              <span
                className={s.omitText}
                style={
                  filteredOptions.find((o) => o.key === value)?.key
                    ? undefined
                    : { color: 'var(--color-gray-75)' }
                }
              >
                {filteredOptions.find((o) => o.key === value)?.label || placeholder || 'Select an option'}
              </span>
              {filteredOptions.find((o) => o.key === value)?.description && (
                <div className={s.description}>
                  {filteredOptions.find((o) => o.key === value)?.description}
                </div>
              )}
            </div>
            <MotionCaret
              animate={{
                rotate: isOpen ? 180 : 0,
                transition: { duration: 0.2 },
              }}
              color="var(--color-gray-75)"
              size={12}
              weight="bold"
            />
          </div>
        </button>
        {isOpen && createPortal(
          <m.div
            layout="size"
            className={s.listAndFilter}
            initial={{ opacity: 0, y: 8 }}
            animate={{
              opacity: 1,
              y: 4,
            }}
            ref={menuRef}
            onBlur={(e: React.FocusEvent<HTMLDivElement>) => e.stopPropagation()}
            tabIndex={-1}
            exit={{ opacity: 0, transition: { duration: 0.1 }, y: 4 }}
          >
            <div className={s.input}>
              <m.input
                layout="size"
                type="text"
                aria-label="Filter"
                placeholder="Filter..."
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilter(e.target.value)}
                onFocus={() => setIsOpen(true)}
                className={s.filter}
              />
              {onClickAddCreditor && (
                <Button variant='secondary' size='sm' className={s.button} onClick={() => {
                  onClickAddCreditor();
                  setIsOpen(false);
                }}>
                  <PlusCircle size={16} weight='fill' color='var(--color-noodle)' />
                  Add new
                </Button>
              )}
            </div>
            <m.ul
              initial={{ opacity: 1, y: 8 }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              role="listbox"
              className={s.list}
              ref={listRef}
              layout="size"
            >
              {filteredOptions?.map((option, index) => (
                <m.li
                  key={option.key + index}
                  onMouseEnter={() => setSelectedIndex(index)}
                  role="option"
                  aria-selected={selectedIndex === index}
                  aria-checked={value === option.key}
                  onClick={() => handleOptionSelect(option.key)}
                  className={s.option}
                  layout="size"
                >
                  <div>
                    <div>{option.label}</div>
                    {option.description && (
                      <span className={s.description}>{option.description}</span>
                    )}
                  </div>
                  {value === option.key && (
                    <Check color="var(--color-primary)" size={12} weight="bold" />
                  )}
                </m.li>
              ))}
            </m.ul>
          </m.div>,
          document.body,
        )}
      </div>
      {!hideLabel && (error || hint) && (
        <p className={error ? s.error : s.hint}>
          {error || hint}
        </p>
      )}
    </div>
  );
};

export default CreditorSelect;